import React, {useEffect, useState} from "react";
import {Button, Card, Form, Input, message, Select} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {
    Organization,
    UserInvite,
    UserResponse
} from "../api-client";
import AccessControl from "../components/common/AccessControl";
import {accessibilityRoles} from "../utils/accessUtils";
import {getUserApi, getMedicalStaffApi, getOrganizationApi} from "../api/apiInstances";
import logger from "../utils/logger";

const RegisterUser: React.FC = () => {
    const intl = useIntl();
    const userApi = getUserApi();
    const medicalApi = getMedicalStaffApi();
    const organizationApi = getOrganizationApi();

    const [form] = Form.useForm();

    const [organizations, setOrganizations] = useState<Organization[]>([]);

    const onFinish = (values: UserInvite) => {
        logger.log('Register form values:', values);
        values.role = "CLINIC_ADMIN";

        medicalApi.createMedicalStaff({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phone: values.phone,
            role: values.role,
            organizationId: values.organizationId
        })
            .then((medicalStaffResponse) => {
                const medicalStaffId = medicalStaffResponse.data.staff.id;

                const userValuesWithMedicalStaffId = {
                    ...values,
                    medicalStaffId: medicalStaffId
                };

                userApi.registerUser(userValuesWithMedicalStaffId)
                    .then((response) => {
                        logger.log('Register response:', response);
                        message.success(intl.formatMessage({ id: 'user_registered_success' }));
                        form.resetFields();
                    })
                    .catch(err => logger.log('Register user error:', err));
            })
            .catch(err => logger.log('Create medical staff error:', err));
    };


    useEffect(() => {
        organizationApi.fetchAllOrganizations('')
            .then(res => {
                setOrganizations(res.data);
            })
            .catch(err => logger.log(err));
    }, []);


    return (
        <AccessControl accessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR]}>
            <Card
                title={<FormattedMessage id="register" />}
                bordered={false}
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="email" />}
                        name="email"
                        rules={[{ required: true, message: intl.formatMessage({ id: 'email_required_error' }) }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="phone" />}
                        name="phone"
                        rules={[{ required: true, message: intl.formatMessage({ id: 'phone_required' }) }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="first_name" />}
                        name="firstName"
                        rules={[{ required: true, message: intl.formatMessage({ id: 'first_name_required' }) }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="last_name" />}
                        name="lastName"
                        rules={[{ required: true, message: intl.formatMessage({ id: 'last_name_required' }) }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="organization" />}
                        name="organizationId"
                        rules={[{ required: true, message: intl.formatMessage({ id: 'organization_required' }) }]}
                    >
                        <Select>
                            {organizations.map((organization: Organization) => (
                                <Select.Option key={organization.id} value={organization.id}>{organization.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="user_role" />}
                        name="role"
                    >
                        <Select disabled={true} defaultValue="CLINIC_ADMIN">
                            <Select.Option key="PLATFORM_ADMINISTRATOR" value="PLATFORM_ADMINISTRATOR">PLATFORM_ADMINISTRATOR</Select.Option>
                            <Select.Option key="CLINIC_ADMIN" value="CLINIC_ADMIN"><FormattedMessage id="CLINIC_ADMIN"/></Select.Option>
                            <Select.Option key="MEDICAL_DOCTOR" value="MEDICAL_DOCTOR">MEDICAL_DOCTOR</Select.Option>
                            <Select.Option key="MEDICAL_ASSISTANT" value="MEDICAL_ASSISTANT">MEDICAL_ASSISTANT</Select.Option>
                            <Select.Option key="RECEPTIONIST" value="RECEPTIONIST">RECEPTIONIST</Select.Option>
                            <Select.Option key="PATIENT" value="PATIENT">PATIENT</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}>
                        <Button type="primary" htmlType="submit" className="dentobook-btn-primary dentobook-btn-medium">
                            <FormattedMessage id="register_user_button" />
                        </Button>
                    </Form.Item>
                </Form>

            </Card>
        </AccessControl>
    )
};

export default RegisterUser;
